import type {
  DataProvider,
  GetListParams,
  GetManyParams,
  GetOneParams,
  RaRecord,
} from "react-admin";
import { API } from "./api";
import { startLoading, stopLoading } from "../../loading";

interface SpringPage<T> {
  content: T[];
  totalElements: number;
}

const resourceConfig: Record<string, { path: string; idField: string }> = {
  "crash/db-rounds": { path: "/db/rounds", idField: "roundId" },
  "crash/db-bets": { path: "/db/bets", idField: "betId" },
  "crash/db-wallet-requests": { path: "/db/wallet-requests", idField: "id" },
  "crash/db-game-configs": { path: "/db/game-configs", idField: "id" },
  "crash/db-operators": { path: "/db/operators", idField: "operatorId" },
  "crash/db-users": { path: "/db/users", idField: "id" },
};

function addId<T extends RaRecord>(
  record: Record<string, unknown>,
  idField: string,
): T {
  return { ...record, id: record[idField] } as T;
}

async function trackedFetch(url: string): Promise<Response> {
  startLoading();
  try {
    return await fetch(url);
  } finally {
    stopLoading();
  }
}

export const crashDataProvider: DataProvider = {
  getList: async <RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: GetListParams,
  ) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const { page = 1, perPage = 50 } = params.pagination ?? {};
    const filter = params.filter ?? {};

    const url = new URL(`${API}${config.path}`);
    url.searchParams.set("page", String(page - 1));
    url.searchParams.set("size", String(perPage));

    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }

    const res = await trackedFetch(url.toString());
    if (!res.ok) throw new Error(`API error ${res.status}`);

    const text = await res.text();
    if (!text || text === "null") return { data: [], total: 0 };
    const json = JSON.parse(text);
    if (Array.isArray(json)) {
      return {
        data: json.map((r: Record<string, unknown>) =>
          addId<RecordType>(r, config.idField),
        ),
        total: json.length,
      };
    }

    const springPage = json as SpringPage<Record<string, unknown>>;
    return {
      data: springPage.content.map((r) => addId<RecordType>(r, config.idField)),
      total: springPage.totalElements,
    };
  },

  getOne: async <RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: GetOneParams,
  ) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const res = await trackedFetch(`${API}${config.path}/${params.id}`);
    if (!res.ok) throw new Error(`API error ${res.status}`);
    const text = await res.text();
    if (!text || text === "null")
      throw new Error(`Resource not found: ${params.id}`);
    const json = JSON.parse(text);
    return { data: addId<RecordType>(json, config.idField) };
  },

  getMany: async <RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: GetManyParams,
  ) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const results = await Promise.all(
      params.ids.map(async (id) => {
        const res = await trackedFetch(`${API}${config.path}/${id}`);
        if (!res.ok) return null;
        const text = await res.text();
        if (!text || text === "null") return null;
        const json = JSON.parse(text);
        return addId<RecordType>(json, config.idField);
      }),
    );
    return {
      data: results.filter((record) => record !== null) as RecordType[],
    };
  },

  getManyReference: async () => ({ data: [], total: 0 }),
  create: async () => {
    throw new Error("Crash records are read-only");
  },
  update: async () => {
    throw new Error("Crash records are read-only");
  },
  updateMany: async () => {
    throw new Error("Crash records are read-only");
  },
  delete: async () => {
    throw new Error("Crash records are read-only");
  },
  deleteMany: async () => {
    throw new Error("Crash records are read-only");
  },
};
